import { useQuery } from "@tanstack/react-query";
import { Ledger, type LedgerItem } from "~/components/charts";
import { api } from "~/lib/api";
import { usePeriod } from "~/lib/period";
import type { PulseMetric, VelocityPulseData } from "@aura/shared";
import { buildHomeDek, buildHomeHeadline, type HomeStoryInput, type Pair } from "../story";
import { ed } from "~/components/editorial";
import { fmtHours, fmtNum, changeText, periodName, priorName, capitalize } from "~/lib/format";

const pair = (m: PulseMetric | null | undefined): Pair | null =>
  m ? { current: m.current, previous: m.previous } : null;

const fmtCount = (v: number | null) => fmtNum(v);
const fmtDur = (v: number | null) => fmtHours(v);
const fmtPct = (v: number | null) => (v === null ? "–" : `${fmtNum(v)}%`);

/**
 * The front page lead: a headline and dek templated from the period's
 * numbers, then a ledger of the team's key measures against the prior
 * period. Reads /metrics/velocity-pulse.
 */
export function VelocityPulse() {
  const { range, window: periodWin } = usePeriod();

  const { data, isLoading, error } = useQuery({
    queryKey: ["velocity-pulse", range],
    queryFn: () =>
      api.get<{ data: VelocityPulseData }>(
        `/metrics/velocity-pulse?range=${range}`,
      ),
    staleTime: 5 * 60 * 1000,
  });

  const pulse = data?.data;
  const now = periodName(periodWin.label);
  const prior = priorName(periodWin.prevLabel);

  if (isLoading) {
    return <div className={ed.loadingSm}>Reading the numbers…</div>;
  }
  if (error || !pulse) {
    return (
      <>
        <div className={ed.kicker}>Autura engineering · {now}</div>
        <h1 className={ed.headline}>Engineering in {now}</h1>
        <p className={ed.dek}>
          The period summary didn't load
          {error ? `: ${String((error as Error).message)}` : "."}
        </p>
      </>
    );
  }

  const m = pulse.metrics;
  const story: HomeStoryInput = {
    periodLabel: periodWin.label,
    prevLabel: periodWin.prevLabel,
    prsMerged: pair(m.prsMerged)!,
    timeToMerge: pair(m.avgTimeToMerge),
    firstReview: pair(m.avgTimeToFirstReview),
    deployments: pair(m.deployments),
    issuesResolved: pair(m.issuesResolved),
    storyPoints: pair(m.storyPoints),
    contributors: pair(m.activeContributors)!,
    momentum: { score: pulse.momentum.score, label: pulse.momentum.label },
  };

  // Six cells across. Deployments and issues drop out when their source
  // isn't connected, and reviews given fills the gap.
  const candidates: Array<LedgerItem | null> = [
    cell("PRs merged", m.prsMerged, fmtCount),
    cell("Deployments", m.deployments, fmtCount, "merges to default branch"),
    m.issuesResolved
      ? cell(
          "Issues resolved",
          m.issuesResolved,
          fmtCount,
          m.storyPoints ? `${fmtNum(m.storyPoints.current)} pts` : undefined,
        )
      : null,
    cell("Open to merge", m.avgTimeToMerge, fmtDur, "average", 0),
    cell("Wait for review", m.avgTimeToFirstReview, fmtDur, "average, first review", 0),
    cell("People shipping", m.activeContributors, fmtCount),
    cell("Reviews given", m.reviewsGiven, fmtCount),
  ];
  const items = candidates.filter((c): c is LedgerItem => c !== null).slice(0, 6);
  const shown = new Set(items.map((i) => i.label));

  const extras: string[] = [];
  if (!shown.has("Reviews given")) {
    extras.push(`${fmtNum(m.reviewsGiven.current)} reviews given (was ${fmtNum(m.reviewsGiven.previous)})`);
  }
  if (m.changeFailureRate) {
    extras.push(
      `change failure rate ${fmtPct(m.changeFailureRate.current)} (was ${fmtPct(m.changeFailureRate.previous)}), a proxy from reverts and hotfixes`,
    );
  }

  return (
    <>
      <div className={ed.kicker}>Autura engineering · {now}</div>
      <h1 className={ed.headline}>{buildHomeHeadline(story)}</h1>
      <p className={ed.dek}>{buildHomeDek(story)}</p>
      <div className={ed.byline}>
        <b>Momentum</b> <span className="num">{pulse.momentum.score}</span> of{" "}
        <span className="num">100</span>, {pulse.momentum.label.toLowerCase()} ·{" "}
        {capitalize(now)} against {prior}. From GitHub and JIRA.
      </div>

      <Ledger items={items} />

      <p className={ed.method}>
        {extras.length > 0 && <>Also: {extras.join("; ")}. </>}
        Momentum weighs each measure's change against {prior} on a 0 to 100
        scale, with 50 meaning level. Times are averages over merged PRs.
      </p>
    </>
  );
}

function cell(
  label: string,
  metric: PulseMetric | null | undefined,
  format: (v: number | null) => string,
  note?: string,
  minBaseForPct?: number,
): LedgerItem | null {
  if (!metric) return null;
  return {
    label,
    current: metric.current,
    prior: metric.previous,
    format,
    note,
    ...changeText(metric.current, metric.previous, { minBaseForPct }),
  };
}
